/**
 * @lodex-pro/web-portal/routes/operator-tank-labels — how the tank-setup and routes pages
 * name a tank's place and size (OPP PR 7). Plain words only: a well by its name (or API
 * number), a battery by its label, never an id.
 */

import type React from 'react';
import type { TankMatchReason } from '@lodex-pro/shared/validation/operator-portal-routes';

interface Place {
  readonly wellId: string | null;
  readonly wellName: string | null;
  readonly apiNumber?: string | null;
  readonly siteLabel: string | null;
}

/** "Smith 1-H", "Well 42-003-12345", "Battery: North Pad", or "No well or battery". */
export function placeLabel(p: Place): string {
  if (p.wellId !== null) {
    if (p.wellName !== null && p.wellName.trim() !== '') return p.wellName;
    if (p.apiNumber !== undefined && p.apiNumber !== null) return `Well ${p.apiNumber}`;
    return 'Unnamed well';
  }
  if (p.siteLabel !== null && p.siteLabel.trim() !== '') return `Battery: ${p.siteLabel}`;
  return 'No well or battery';
}

/** A stable grouping key for a tank's place (well id, else battery label). */
export function placeKey(p: Place): string {
  if (p.wellId !== null) return `well:${p.wellId}`;
  return `battery:${(p.siteLabel ?? '').trim().toLowerCase()}`;
}

/** "400 BBL" or "Capacity not set". Capacity is a decimal string from the API. */
export function capacityLabel(capacityBbl: string | null): string {
  if (capacityBbl === null || capacityBbl === '') return 'Capacity not set';
  const n = Number(capacityBbl);
  if (Number.isNaN(n)) return `${capacityBbl} BBL`;
  return `${n.toLocaleString()} BBL`;
}

/** "1 tank" / "3 tanks". */
export function plural(n: number, one: string, many: string = `${one}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/** An ISO instant in the viewer's local date and time (DEC-004: never UTC). */
export function localDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const MUTED: React.CSSProperties = { color: 'var(--color-text-tertiary)' };

export const BUTTON: React.CSSProperties = {
  padding: '6px 12px',
  cursor: 'pointer',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: 'var(--radius-pill)',
  fontSize: '13px',
};

export const PRIMARY_BUTTON: React.CSSProperties = {
  ...BUTTON,
  background: 'var(--color-accent-blue)',
  border: '1px solid var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};

export const INPUT: React.CSSProperties = {
  background: 'var(--color-bg-secondary)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  padding: '6px 10px',
  fontSize: '13px',
};

export const CARD: React.CSSProperties = {
  background: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '12px 16px',
};

export const TAG: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 'var(--radius-pill)',
  fontSize: '12px',
  background: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  color: 'var(--color-text-secondary)',
};

// ── Tank registry PR 1 (0245) ─────────────────────────────────────────────────

interface RegistryPlace {
  readonly wellId: string | null;
  readonly wellName: string | null;
  readonly apiNumber: string | null;
  readonly siteLabel: string | null;
  readonly batteryId: string | null;
  readonly batteryName: string | null;
}

/**
 * Where one of the company's own tanks sits: exactly one of a catalog well ("Smith 1H · API
 * 42-003-12345"), a battery ("Battery: North Pad") or a site label ("Site: East yard").
 */
export function registryPlaceLabel(p: RegistryPlace): string {
  if (p.wellId !== null) {
    const name = p.wellName !== null && p.wellName.trim() !== '' ? p.wellName : 'Unnamed well';
    return p.apiNumber !== null && p.apiNumber !== '' ? `${name} · API ${p.apiNumber}` : name;
  }
  if (p.batteryId !== null) return `Battery: ${p.batteryName ?? 'unnamed'}`;
  if (p.siteLabel !== null && p.siteLabel.trim() !== '') return `Site: ${p.siteLabel}`;
  return 'No well, battery or site';
}

/** A decimal string from the API as a plain number ("1.67"), or null when not set. */
export function decimalLabel(v: string | null): string | null {
  if (v === null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? v : n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/** True when both sizes are set and are not the same number. */
export function sizesDiffer(a: string | null, b: string | null): boolean {
  if (a === null || b === null || a === '' || b === '') return false;
  const x = Number(a);
  const y = Number(b);
  return Number.isFinite(x) && Number.isFinite(y) && x !== y;
}

export const WARNING: React.CSSProperties = {
  ...TAG,
  background: 'rgba(246, 182, 94, 0.12)',
  border: '1px solid rgba(246, 182, 94, 0.4)',
  color: 'var(--color-warning)',
};

// ── Tank registry PR 3 (0249) ─────────────────────────────────────────────────

/**
 * How a hauler's tank found the company's tank: "Matched by ID T-301", "Matched by tag T-301"
 * or "Matched by name"; null when it matched nothing.
 */
export function matchReasonLabel(
  reason: TankMatchReason | null,
  producerIdentifier: string | null,
): string | null {
  if (reason === null) return null;
  if (reason === 'NAME') return 'Matched by name';
  const ident = producerIdentifier?.trim() ?? '';
  // 13.8 (D-20b): ID is the operator tank's producer tank ID, no longer its name.
  const how = reason === 'ID' ? 'Matched by producer tank ID' : 'Matched by tag';
  return ident === '' ? how : `${how} ${ident}`;
}

/** "400 BBL · 1.67 BBL/in", with "not set" for either size missing. */
export function sizesLabel(capacityBbl: string | null, bblPerInch: string | null): string {
  const bpi = decimalLabel(bblPerInch);
  return `${capacityLabel(capacityBbl)} · ${bpi === null ? 'BBL per inch not set' : `${bpi} BBL/in`}`;
}
